import { AuthRepository } from '../../domain/repositories/auth_repository';
import { TokenEntity, LoginCredentials, LoginResponse } from '../../domain/entities/token_entity';
import { AuthLocalDataSource } from '../datasources/local/auth_local_datasource';
import { AuthRemoteDataSource } from '../datasources/remote/auth_remote_datasource';
import { NetworkException, ServerException, CacheException, InvalidTokenException } from '../exceptions/data_exceptions';

/**
 * Implementação do repositório de autenticação
 * Coordena entre datasources locais e remotos
 */
export class AuthRepositoryImpl implements AuthRepository {
  constructor(
    private localDataSource: AuthLocalDataSource,
    private remoteDataSource: AuthRemoteDataSource
  ) {}

  async getPersistedToken(): Promise<TokenEntity | null> {
    try {
      return await this.localDataSource.getToken();
    } catch (error) {
      throw new CacheException(`Failed to get persisted token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async persistToken(tokenEntity: TokenEntity | null): Promise<void> {
    try {
      await this.localDataSource.saveToken(tokenEntity);
    } catch (error) {
      throw new CacheException(`Failed to persist token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      return await this.remoteDataSource.login(credentials);
    } catch (error) {
      if (error instanceof Error && error.message.includes('Network')) {
        throw new NetworkException(`Login failed due to network error: ${error.message}`);
      }
      throw new ServerException(`Login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async logout(): Promise<void> {
    console.log('🚪 AuthRepository: Iniciando logout...');
    
    try {
      // Primeiro tenta fazer logout no servidor
      console.log('🌐 Tentando logout no servidor...');
      await this.remoteDataSource.logout();
      console.log('✅ Logout no servidor realizado com sucesso');
    } catch (error) {
      // Se falhar no servidor, apenas loga o erro mas continua com a limpeza local
      console.warn('⚠️ Remote logout failed:', error);
    } finally {
      // Sempre limpa o token local
      console.log('🗑️ Removendo credenciais locais...');
      await this.localDataSource.removeToken();
      console.log('✅ Credenciais locais removidas');
    }
  }

  async refreshToken(): Promise<TokenEntity | null> {
    try {
      const currentToken = await this.localDataSource.getToken();
      
      if (!currentToken) {
        return null;
      }

      // Em uma implementação real, você teria um refresh token separado
      // Por agora, vamos assumir que o token atual pode ser usado para refresh
      const response = await this.remoteDataSource.refreshToken(currentToken.token);
      
      const newToken: TokenEntity = {
        token: response.token,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24h default
      };

      await this.localDataSource.saveToken(newToken);
      return newToken;
    } catch (error) {
      await this.localDataSource.removeToken();
      throw new InvalidTokenException(`Token refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  isTokenValid(token: TokenEntity): boolean {
    if (!token.token) {
      return false;
    }

    if (token.expiresAt) {
      return token.expiresAt > new Date();
    }

    // Se não tem data de expiração, assume que é válido
    return true;
  }
}
